
class CustomerStoriesMarquee extends HTMLElement {
    constructor() {
        super();
        this.swiper = null;
        this.retryCount = 0;
        this.maxRetries = 10;
        this.isSafariDevice = this.isSafari();
        this.animationId = null;
    }

    connectedCallback() {
        if (this.isSafariDevice) {
            this.initializeSafariMarquee();
        } else {
            setTimeout(() => {
                this.waitForSwiper().then(() => {
                    this.initializeSlider();
                });
            }, 500);
        }
    }

    disconnectedCallback() {
        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
    }

    isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
               /iPad|iPhone|iPod/.test(navigator.userAgent) ||
               (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    initializeSafariMarquee() {
        console.log('Initializing Safari marquee');
        const swiperElement = this.querySelector('.customer-stories__slider');
        const swiperWrapper = this.querySelector('.swiper-wrapper');
        
        if (!swiperElement || !swiperWrapper) {
            console.warn('Safari marquee elements not found');
            return;
        }

        // Add Safari-specific CSS
        this.addSafariStyles();

        // Clone slides for infinite loop effect
        this.duplicateSlidesForSafari();

        // Apply CSS animation
        this.applySafariAnimation();

        // Add intersection observer to restart animation when in viewport
        this.setupVisibilityObserver();
    }

    addSafariStyles() {
        if (!document.querySelector('#safari-customer-stories-styles')) {
            const style = document.createElement('style');
            style.id = 'safari-customer-stories-styles';
            style.textContent = `
                .safari-marquee .swiper-wrapper {
                    display: flex !important;
                    animation: safariMarquee 60s linear infinite !important;
                    width: max-content !important;
                    will-change: transform;
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                }
                
                .safari-marquee .swiper-slide {
                    flex-shrink: 0 !important;
                    width: auto !important;
                    margin-right: 16px !important;
                }
                
                .safari-marquee .customer-stories__slide-image {
                    width: 300px !important;
                    height: auto !important;
                    object-fit: cover !important;
                }
                
                @keyframes safariMarquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                
                @media (max-width: 768px) {
                    .safari-marquee .customer-stories__slide-image {
                        width: 250px !important;
                    }
                    .safari-marquee .swiper-slide {
                        margin-right: 12px !important;
                    }
                }
                
                .safari-marquee .swiper {
                    overflow: hidden !important;
                    mask: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
                    -webkit-mask: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
                }
            `;
            document.head.appendChild(style);
        }
    }

    duplicateSlidesForSafari() {
        const swiperWrapper = this.querySelector('.swiper-wrapper');
        const originalSlides = [...swiperWrapper.children];
        
        if (originalSlides.length === 0) return;

        // Clone slides multiple times for smooth infinite loop
        for (let i = 0; i < 3; i++) {
            originalSlides.forEach(slide => {
                const clone = slide.cloneNode(true);
                clone.setAttribute('data-clone', 'true');
                swiperWrapper.appendChild(clone);
            });
        }
    }

    applySafariAnimation() {
        const slider = this.querySelector('.customer-stories__slider');
        if (slider) {
            slider.classList.add('safari-marquee');
            
            // Force restart animation
            const wrapper = this.querySelector('.swiper-wrapper');
            if (wrapper) {
                wrapper.style.animationPlayState = 'running';
                
                // Restart animation on interaction
                wrapper.addEventListener('animationiteration', () => {
                    console.log('Safari marquee animation iteration');
                });
            }
        }
    }

    setupVisibilityObserver() {
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const wrapper = this.querySelector('.swiper-wrapper');
                    if (wrapper) {
                        if (entry.isIntersecting) {
                            wrapper.style.animationPlayState = 'running';
                        } else {
                            wrapper.style.animationPlayState = 'paused';
                        }
                    }
                });
            }, { threshold: 0.1 });

            observer.observe(this);
        }
    }

    waitForSwiper() {
        return new Promise((resolve) => {
            const checkSwiper = () => {
                if (window.Swiper && this.retryCount < this.maxRetries) {
                    resolve();
                } else if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    setTimeout(checkSwiper, 200);
                } else {
                    console.warn('Swiper library not loaded after maximum retries');
                    resolve();
                }
            };
            checkSwiper();
        });
    }

    initializeSlider() {
        const swiperElement = this.querySelector('.customer-stories__slider');

        if (!swiperElement || !window.Swiper) {
            console.warn('Swiper element or library not found');
            return;
        }

        try {
            const speed = parseInt(this.getAttribute('speed') || '3000');
            
            this.swiper = new Swiper(swiperElement, {
                slidesPerView: 'auto',
                spaceBetween: 16,
                loop: true,
                speed: speed,
                centeredSlides: false,
                freeMode: false,
                allowTouchMove: true,
                simulateTouch: true,
                touchRatio: 1,
                touchAngle: 45,
                grabCursor: false,
                autoplay: {
                    delay: 10,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                    reverseDirection: false,
                    stopOnLastSlide: false,
                    waitForTransition: false
                },
                resistance: false,
                resistanceRatio: 0,
                updateOnWindowResize: true,
                observer: true,
                observeParents: true,
                watchSlidesProgress: true,
                passiveListeners: true,
                breakpoints: {
                    320: {
                        slidesPerView: 'auto',
                        spaceBetween: 12
                    },
                    640: {
                        slidesPerView: 'auto',
                        spaceBetween: 16
                    },
                    768: {
                        slidesPerView: 'auto',
                        spaceBetween: 16
                    },
                    1024: {
                        slidesPerView: 'auto',
                        spaceBetween: 20
                    }
                },
                on: {
                    init: () => {
                        console.log('Swiper initialized for customer stories');
                    }
                }
            });

        } catch (error) {
            console.error('Error initializing customer stories slider:', error);
        }
    }

    // Public methods for external control
    startAutoplay() {
        if (this.isSafariDevice) {
            const wrapper = this.querySelector('.swiper-wrapper');
            if (wrapper) {
                wrapper.style.animationPlayState = 'running';
            }
        } else if (this.swiper && this.swiper.autoplay) {
            this.swiper.autoplay.start();
        }
    }

    stopAutoplay() {
        if (this.isSafariDevice) {
            const wrapper = this.querySelector('.swiper-wrapper');
            if (wrapper) {
                wrapper.style.animationPlayState = 'paused';
            }
        } else if (this.swiper && this.swiper.autoplay) {
            this.swiper.autoplay.stop();
        }
    }

    updateSlider() {
        if (this.swiper) {
            this.swiper.update();
        }
    }
}

// Register the custom element
if (!customElements.get('customer-stories-marquee')) {
    customElements.define('customer-stories-marquee', CustomerStoriesMarquee);
}